import { useMemo, useState } from "react";

const EVENTS_STEP = 8;

function PartidoPlayByPlaySection({ playByPlay }) {
    const periodFilters = playByPlay?.periodFilters || ["TODOS", "1C", "2C", "3C", "4C", "TE"];
    const [activeFilter, setActiveFilter] = useState("TODOS");
    const [visibleCount, setVisibleCount] = useState(EVENTS_STEP);

    const filteredEvents = useMemo(() => {
        const events = playByPlay?.events || [];
        if (activeFilter === "TODOS") {
            return events;
        }

        return events.filter((event) => event.periodKey === activeFilter);
    }, [activeFilter, playByPlay]);

    const visibleEvents = filteredEvents.slice(0, visibleCount);
    const hasMoreEvents = filteredEvents.length > visibleCount;
    const hasEvents = (playByPlay?.events || []).length > 0;

    return (
        <section className="flex-1 bg-[#102216] rounded-xl p-3 sm:p-4 md:p-6 border border-white/10">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4 sm:mb-5">
                <h3 className="text-lg sm:text-2xl font-bold flex items-center gap-2 text-slate-100">
                    <span className="w-1.5 sm:w-2 h-6 sm:h-8 bg-green-500 rounded-full" />
                    Jugada a Jugada
                </h3>

                {hasEvents && <div className="inline-flex items-center bg-[#0b1b12] border border-white/10 rounded-xl p-0.5 sm:p-1 overflow-x-auto w-fit">
                    {periodFilters.map((filter) => (
                        <button
                            key={filter}
                            type="button"
                            onClick={() => {
                                setActiveFilter(filter);
                                setVisibleCount(EVENTS_STEP);
                            }}
                            className={`px-2.5 sm:px-4 py-1.5 sm:py-2 text-[10px] sm:text-xs font-bold tracking-[0.12em] rounded-lg transition-colors whitespace-nowrap ${
                                activeFilter === filter
                                    ? "bg-green-500 text-[#062a1a]"
                                    : "text-slate-300 hover:text-white"
                            }`}
                        >
                            {filter}
                        </button>
                    ))}
                </div>}
            </div>

            {visibleEvents.length ? (
                <div className="h-[20rem] sm:h-[28rem] md:h-[36rem] overflow-y-auto pr-1 scrollbar-hide sm:[&]:scrollbar-default">
                    <div className="space-y-3 sm:space-y-4">
                        {visibleEvents.map((event) => {
                        const indicatorColorClass = event.isScoringPlay
                            ? (event.isSuccessful ? "text-green-500 border-green-500/30" : "text-red-500 border-red-500/30")
                            : "text-white border-white/30";

                        return (
                            <article key={event.id} className="flex items-start gap-1.5 sm:gap-3 md:gap-4">
                            <div className="w-8 sm:w-14 text-slate-400 text-[10px] sm:text-sm font-bold tracking-wide pt-2 sm:pt-3 text-right shrink-0">{event.clock}</div>

                            <div className={`w-6 h-6 sm:w-10 sm:h-10 rounded-full border bg-[#0b1b12] flex items-center justify-center shrink-0 mt-1 ${indicatorColorClass}`}>
                                {event.isGameEnd ? (
                                    <svg viewBox="0 0 24 24" className="w-3 h-3 sm:w-5 sm:h-5" fill="currentColor" aria-hidden="true">
                                        <path d="M12 8a1 1 0 0 1 1 1v3.2l2.4 1.44a1 1 0 0 1-1.03 1.72l-2.89-1.74A1 1 0 0 1 11 13V9a1 1 0 0 1 1-1Zm0-6a10 10 0 1 1 0 20 10 10 0 0 1 0-20Zm0 2a8 8 0 1 0 0 16 8 8 0 0 0 0-16Z" />
                                    </svg>
                                ) : (
                                    <span className="w-1.5 h-1.5 sm:w-3 sm:h-3 rounded-full bg-current" />
                                )}
                            </div>

                            <div className="flex-1 rounded-xl border border-white/10 bg-[#0f1f15] px-2.5 sm:px-4 py-2 sm:py-3 flex items-center justify-between gap-1.5 sm:gap-3 min-w-0">
                                <div className="min-w-0">
                                    <p className="text-slate-100 font-bold tracking-[0.08em] sm:tracking-[0.12em] uppercase text-[10px] sm:text-sm md:text-base truncate">
                                        {event.isGameEnd ? "Final del partido" : event.description}
                                    </p>

                                    {!event.isGameEnd ? (
                                        <div className="mt-0.5 sm:mt-1 flex items-center gap-2 min-w-0">
                                            {event.player ? (
                                                <span className="text-slate-300 text-xs sm:text-sm truncate">{event.player}</span>
                                            ) : null}
                                        </div>
                                    ) : null}

                                    {!event.isGameEnd && (event.playerStatSnapshot || event.detail) ? (
                                        <p className="text-slate-500 text-[10px] mt-0.5 sm:mt-1 uppercase tracking-[0.1em] font-bold truncate hidden sm:block">
                                            {[event.playerStatSnapshot, event.detail].filter(Boolean).join(" • ")}
                                        </p>
                                    ) : null}
                                </div>

                                <div className="text-right shrink-0">
                                    <p className="text-slate-100 font-black text-sm sm:text-xl tabular-nums">{event.scoreLabel}</p>
                                    {!event.isGameEnd && event.isScoringPlay ? (
                                        <p className={`text-[9px] sm:text-[10px] mt-0.5 sm:mt-1 font-bold tracking-[0.12em] uppercase ${event.isSuccessful ? "text-green-400" : "text-red-400"}`}>
                                            {event.isSuccessful ? "Convertido" : "Errado"}
                                        </p>
                                    ) : null}
                                    <p className="text-slate-500 text-[9px] sm:text-[10px] mt-0.5 sm:mt-1 font-bold tracking-[0.12em] uppercase">{event.periodLabel}</p>
                                </div>
                            </div>
                            </article>
                        );
                        })}

                        {hasMoreEvents ? (
                            <div className="pt-1 flex justify-center">
                                <button
                                    type="button"
                                    className="px-6 py-3 rounded-full bg-[#24392f] text-slate-100 font-semibold text-sm hover:bg-[#2b4438] transition-colors"
                                    onClick={() => setVisibleCount((current) => current + EVENTS_STEP)}
                                >
                                    Cargar más jugadas
                                </button>
                            </div>
                        ) : null}
                    </div>
                </div>
            ) : (
                <div className="rounded-lg border border-white/10 bg-[#0f1f15] p-6 text-slate-300 text-sm text-center">
                    No hay datos de jugada a jugada para mostrar.
                </div>
            )}
        </section>
    );
}

export default PartidoPlayByPlaySection;
